/**
 * Brandner WebSocket Client (via Pusher) mit BroadcastChannel Tab-Sharing
 *
 * Architektur:
 * - EIN Tab pro Browser-Session haelt die Pusher-Verbindung ("Leader")
 * - Alle anderen Tabs empfangen Events via BroadcastChannel ("Follower")
 * - Leader-Election: Erster Tab wird Leader, bei Tab-Close uebernimmt ein anderer
 * - Fallback: Ohne BroadcastChannel-Support → jeder Tab eigene Verbindung (wie vorher)
 *
 * Spart Pusher-Connections: 1 User mit 5 Tabs = 1 Connection statt 5
 *
 * @version 3.0.0
 */
(function($) {
    'use strict';

    // Konfiguration aus wp_localize_script
    var config = window.brandnerWebSocket || {};

    // Guard: Nur wenn WebSocket aktiviert
    if (!config.enabled || !config.pusherKey) {
        return;
    }

    var DEBUG = config.debug || false;
    function debugLog() { if (DEBUG) console.log.apply(console, ['[BrandnerWS]'].concat(Array.prototype.slice.call(arguments))); }
    function debugWarn() { if (DEBUG) console.warn.apply(console, ['[BrandnerWS]'].concat(Array.prototype.slice.call(arguments))); }

    var CHANNEL_NAME = 'auctions';

    // =========================================================================
    // BroadcastChannel Tab-Sharing
    // =========================================================================

    var BC_CHANNEL_NAME = 'brandner-ws';
    var bc = null;
    var isLeader = false;
    var myTabId = Math.random().toString(36).substr(2, 9);
    var leaderLastSeen = 0;
    var heartbeatInterval = null;
    var leaderCheckInterval = null;
    var HEARTBEAT_MS = 4000;        // Leader sendet alle 4s
    var LEADER_TIMEOUT_MS = 6000;   // Follower wartet max 6s auf Heartbeat
    var ELECTION_WAIT_MS = 800;     // Wartezeit bei Leader-Election
    var electionTimeout = null;

    var BrandnerWebSocket = {
        pusher: null,
        channel: null,
        connected: false,
        receiving: false,  // true wenn Follower Updates via BC bekommt
        idleTimeout: null,
        idleDisconnected: false,

        // Polling-Intervalle
        NORMAL_POLL_MS: 3000,   // Standard-Polling (Fallback)
        SLOW_POLL_MS: 60000,    // Health-Check wenn WS/BC aktiv (60s reicht, WS liefert Echtzeit)
        CRITICAL_POLL_MS: 1500, // Letzte 30s vor Auktionsende
        IDLE_DISCONNECT_MS: 120000, // 2 Min im Hintergrund → disconnect

        // =================================================================
        // Initialization
        // =================================================================

        init: function() {
            // Guard: Pusher JS-SDK geladen?
            if (typeof Pusher === 'undefined') {
                debugWarn('Pusher SDK nicht geladen — Fallback auf Polling');
                return;
            }

            // Guard: Sind ueberhaupt Auktionen auf der Seite?
            var hasProducts = (window.brandnerProductRealtime && window.brandnerProductRealtime.productId) ||
                              $('.brandner-auction-card-bid').length > 0;
            if (!hasProducts) {
                debugLog('Keine Auktionen auf Seite — kein Pusher noetig');
                return;
            }

            // BroadcastChannel verfuegbar? → Tab-Sharing starten
            if (typeof BroadcastChannel !== 'undefined') {
                this.initBroadcastChannel();
            } else {
                // Kein BroadcastChannel (IE11, alte Safari) → direkt Leader
                debugLog('Kein BroadcastChannel — jeder Tab eigene Verbindung');
                isLeader = true;
                this.initAsPusherLeader();
            }
        },

        // =================================================================
        // BroadcastChannel: Leader-Election
        // =================================================================

        initBroadcastChannel: function() {
            var self = this;

            try {
                bc = new BroadcastChannel(BC_CHANNEL_NAME);
            } catch (e) {
                debugWarn('BroadcastChannel Fehler:', e.message);
                isLeader = true;
                this.initAsPusherLeader();
                return;
            }

            bc.onmessage = function(event) {
                self.handleBCMessage(event.data);
            };

            // Frage ob schon ein Leader existiert
            bc.postMessage({ type: 'request-leader', tabId: myTabId });
            debugLog('Tab', myTabId, '— suche Leader...');

            // Warte auf Antwort — wenn keine kommt, werde selbst Leader
            electionTimeout = setTimeout(function() {
                if (!isLeader && !self.receiving) {
                    self.becomeLeader();
                }
            }, ELECTION_WAIT_MS);
        },

        handleBCMessage: function(msg) {
            if (!msg || !msg.type) return;

            var self = this;

            switch (msg.type) {
                case 'leader-announce':
                    // Ein Leader hat sich gemeldet
                    if (msg.tabId !== myTabId) {
                        leaderLastSeen = Date.now();
                        if (electionTimeout) {
                            clearTimeout(electionTimeout);
                            electionTimeout = null;
                        }
                        if (!isLeader) {
                            this.becomeFollower();
                        } else {
                            // Ich bin auch Leader — der andere war schneller, ich trete zurueck
                            debugLog('Anderer Leader gefunden — trete zurueck');
                            this.stepDownAsLeader();
                        }
                    }
                    break;

                case 'leader-heartbeat':
                    if (msg.tabId !== myTabId) {
                        leaderLastSeen = Date.now();
                    }
                    break;

                case 'request-leader':
                    // Ein neuer Tab fragt nach Leader
                    if (isLeader) {
                        bc.postMessage({ type: 'leader-announce', tabId: myTabId });
                    }
                    break;

                case 'leader-gone':
                    // Leader-Tab wird geschlossen — neue Election
                    if (!isLeader) {
                        debugLog('Leader weg — starte Election');
                        this.startElection();
                    }
                    break;

                case 'pusher-event':
                    // Pusher-Event vom Leader empfangen (nur als Follower)
                    if (!isLeader && msg.event && msg.data) {
                        this.handleRelayedEvent(msg.event, msg.data);
                    }
                    break;
            }
        },

        becomeLeader: function() {
            if (isLeader) return;

            isLeader = true;
            this.receiving = false;
            debugLog('Tab', myTabId, '→ LEADER');

            // Allen anderen mitteilen
            if (bc) {
                bc.postMessage({ type: 'leader-announce', tabId: myTabId });
            }

            // Heartbeat starten
            this.startHeartbeat();

            // Pusher initialisieren
            this.initAsPusherLeader();
        },

        becomeFollower: function() {
            if (this.receiving) return; // Schon Follower

            this.receiving = true;
            debugLog('Tab', myTabId, '→ FOLLOWER (Updates via BroadcastChannel)');

            // Polling verlangsamen (Updates kommen via BC)
            this.slowDownPolling();

            // Leader-Ueberwachung starten
            this.startLeaderCheck();
        },

        stepDownAsLeader: function() {
            isLeader = false;

            // Heartbeat stoppen
            if (heartbeatInterval) {
                clearInterval(heartbeatInterval);
                heartbeatInterval = null;
            }

            // Pusher trennen
            if (this.pusher) {
                this.pusher.disconnect();
                this.pusher = null;
                this.channel = null;
                this.connected = false;
            }

            // Als Follower weitermachen
            this.becomeFollower();
        },

        startElection: function() {
            // Zufaellige Wartezeit um Race-Conditions zu vermeiden
            var delay = 100 + Math.random() * 400;
            var self = this;

            if (electionTimeout) {
                clearTimeout(electionTimeout);
            }

            electionTimeout = setTimeout(function() {
                // Pruefen ob inzwischen ein anderer Leader geworden ist
                if (Date.now() - leaderLastSeen < LEADER_TIMEOUT_MS) {
                    debugLog('Neuer Leader gefunden waehrend Election');
                    return;
                }
                self.becomeLeader();
            }, delay);
        },

        startHeartbeat: function() {
            if (heartbeatInterval) {
                clearInterval(heartbeatInterval);
            }

            heartbeatInterval = setInterval(function() {
                if (bc && isLeader) {
                    bc.postMessage({ type: 'leader-heartbeat', tabId: myTabId });
                }
            }, HEARTBEAT_MS);
        },

        startLeaderCheck: function() {
            if (leaderCheckInterval) {
                clearInterval(leaderCheckInterval);
            }

            var self = this;
            leaderLastSeen = Date.now(); // Erstmal annehmen Leader lebt

            leaderCheckInterval = setInterval(function() {
                if (isLeader) {
                    clearInterval(leaderCheckInterval);
                    leaderCheckInterval = null;
                    return;
                }

                if (Date.now() - leaderLastSeen > LEADER_TIMEOUT_MS) {
                    debugLog('Leader-Heartbeat ausgeblieben — starte Election');
                    clearInterval(leaderCheckInterval);
                    leaderCheckInterval = null;
                    self.startElection();
                }
            }, LEADER_TIMEOUT_MS);
        },

        // =================================================================
        // Pusher Connection (nur fuer Leader-Tab)
        // =================================================================

        initAsPusherLeader: function() {
            var self = this;

            debugLog('Initialisiere Pusher mit Key:', config.pusherKey, 'Cluster:', config.pusherCluster);

            try {
                this.pusher = new Pusher(config.pusherKey, {
                    cluster: config.pusherCluster || 'eu',
                    forceTLS: true,
                });
            } catch (e) {
                debugWarn('Pusher Init-Fehler:', e.message);
                return;
            }

            this.bindConnectionEvents();
            this.subscribe();
            this.initVisibilityHandler();
            this.initBeforeUnload();

            debugLog('Pusher initialisiert — 1 Channel fuer alle Auktionen');
        },

        /**
         * Connection-State Events: Polling anpassen
         */
        bindConnectionEvents: function() {
            var self = this;

            this.pusher.connection.bind('connected', function() {
                debugLog('WebSocket verbunden');
                self.connected = true;
                self.slowDownPolling();
            });

            this.pusher.connection.bind('disconnected', function() {
                debugLog('WebSocket getrennt — Polling beschleunigen');
                self.connected = false;
                self.restorePolling();
            });

            this.pusher.connection.bind('error', function(err) {
                debugWarn('WebSocket Fehler:', err);
                self.connected = false;
                self.restorePolling();
            });

            this.pusher.connection.bind('connecting', function() {
                debugLog('WebSocket reconnecting...');
            });
        },

        /**
         * Subscribe zum einzigen "auctions" Channel und binde alle Events
         */
        subscribe: function() {
            if (!this.pusher) return;

            this.channel = this.pusher.subscribe(CHANNEL_NAME);
            var self = this;

            this.channel.bind('bid-update', function(data) {
                var pid = data.product_id;
                debugLog('bid-update fuer', pid, ':', data.current_bid_formatted);
                self.handleBidUpdate(pid, data);
                self.broadcastEvent('bid-update', data);
            });

            this.channel.bind('timer-update', function(data) {
                var pid = data.product_id;
                debugLog('timer-update fuer', pid, '+' + data.extended_by + 's');
                self.handleTimerUpdate(pid, data);
                self.broadcastEvent('timer-update', data);
            });

            this.channel.bind('auction-ended', function(data) {
                var pid = data.product_id;
                debugLog('auction-ended fuer', pid);
                self.handleAuctionEnded(pid, data);
                self.broadcastEvent('auction-ended', data);
            });

            debugLog('Subscribed zu Channel:', CHANNEL_NAME);
        },

        /**
         * Pusher-Event an alle Follower-Tabs weiterleiten
         */
        broadcastEvent: function(eventName, data) {
            if (bc && isLeader) {
                bc.postMessage({
                    type: 'pusher-event',
                    event: eventName,
                    data: data
                });
            }
        },

        /**
         * Event vom Leader empfangen (Follower-Tab)
         */
        handleRelayedEvent: function(eventName, data) {
            var pid = data.product_id;
            debugLog('[BC]', eventName, 'fuer', pid);

            if (eventName === 'bid-update') {
                this.handleBidUpdate(pid, data);
            } else if (eventName === 'timer-update') {
                this.handleTimerUpdate(pid, data);
            } else if (eventName === 'auction-ended') {
                this.handleAuctionEnded(pid, data);
            }
        },

        // =====================================================================
        // Event-Handler: Rufen bestehende Update-Methoden auf
        // =====================================================================

        /**
         * Neues Gebot empfangen — aktualisiert Cards oder Einzelprodukt-Seite
         */
        handleBidUpdate: function(productId, data) {
            // Client-seitig is_winning berechnen (kein Private Channel noetig)
            var myUserId = this.getCurrentUserId();
            data.is_winning = (myUserId > 0 && parseInt(data.current_bidder) === myUserId);
            // user_has_bid: Aus Card-Attribut lesen (Pusher kennt nur den Gewinner)
            var existingCard = $('.brandner-auction-card-bid[data-product-id="' + productId + '"]');
            data.user_has_bid = data.is_winning || (existingCard.length > 0 && existingCard.attr('data-user-has-bid') === '1');
            data.changed = true;

            // Loop-Seite: BrandnerRealtime.updateCards()
            if (window.BrandnerRealtime && window.BrandnerRealtime._initialized) {
                var auctions = {};
                auctions[productId] = data;
                window.BrandnerRealtime.updateCards(auctions, 0);
            }

            // Single Product: syncServerTime + updatePage.
            // Kein pauschales Verwerfen im optimistischen Fenster mehr — updatePage
            // verwirft nur noch echte Stale-Daten (current_bid unter dem eigenen Gebot)
            // und wendet autoritative Updates (eigener Proxy-Bump, Ueberbietung) an.
            if (window.BrandnerProductRealtime && window.BrandnerProductRealtime.initialized) {
                if (parseInt(window.BrandnerProductRealtime.productId) === parseInt(productId)) {
                    window.BrandnerProductRealtime.syncServerTime(data.remaining, data);
                    var applied = window.BrandnerProductRealtime.updatePage(data);
                    if (applied !== false) {
                        window.BrandnerProductRealtime.lastHash = data.hash;
                    }
                }
            }
        },

        /**
         * Timer-Extension (Anti-Sniping) — aktualisiert End-Timestamp
         */
        handleTimerUpdate: function(productId, data) {
            // Loop-Seite: NUR den Timer syncen. Der timer-update-Payload traegt
            // keine Bid-/Statusfelder (is_winning, current_bid, ...) — ueber
            // updateCards() wurde is_winning=undefined als "nicht fuehrend"
            // interpretiert und der Hoechstbietende auf "Ueberboten" geflippt
            // (plus Hash-Cache-Vergiftung). syncTimer setzt data-end-timestamp
            // selbst; das Attribut darf hier nicht vorab geschrieben werden,
            // sonst erkennt syncTimer die Verlaengerung nicht (+Xs-Badge).
            var $card = $('.brandner-auction-card-bid[data-product-id="' + productId + '"]');
            if ($card.length) {
                if (window.BrandnerRealtime && window.BrandnerRealtime._initialized) {
                    window.BrandnerRealtime.syncTimer(productId, data, 0);
                } else {
                    $card.attr('data-end-timestamp', data.end_timestamp);
                }
            }

            // Single Product: syncServerTime erkennt Anti-Sniping automatisch
            if (window.BrandnerProductRealtime && window.BrandnerProductRealtime.initialized) {
                if (parseInt(window.BrandnerProductRealtime.productId) === parseInt(productId)) {
                    window.BrandnerProductRealtime.syncServerTime(data.remaining, data);
                }
            }
        },

        /**
         * Auktion beendet — Status aktualisieren
         */
        handleAuctionEnded: function(productId, data) {
            data.status = 'ended';
            data.changed = true;

            var myUserId = this.getCurrentUserId();
            data.is_winning = (myUserId > 0 && parseInt(data.current_bidder) === myUserId);
            // user_has_bid: Aus Card-Attribut lesen (oeffentlicher Pusher-Payload kennt
            // nur den Gewinner) — gleiches Muster wie handleBidUpdate. Ohne dieses Feld
            // wuerde das persistente "Ueberboten"-Tag im Pusher-Pfad nicht gesetzt.
            var existingCard = $('.brandner-auction-card-bid[data-product-id="' + productId + '"]');
            data.user_has_bid = data.is_winning || (existingCard.length > 0 && existingCard.attr('data-user-has-bid') === '1');

            // Loop-Seite
            if (window.BrandnerRealtime && window.BrandnerRealtime._initialized) {
                var auctions = {};
                auctions[productId] = data;
                window.BrandnerRealtime.updateCards(auctions, 0);
            }

            // Single Product
            if (window.BrandnerProductRealtime && window.BrandnerProductRealtime.initialized) {
                if (parseInt(window.BrandnerProductRealtime.productId) === parseInt(productId)) {
                    window.BrandnerProductRealtime.updatePage(data);
                }
            }
        },

        // =====================================================================
        // Polling-Steuerung: verlangsamen / wiederherstellen
        // =====================================================================

        slowDownPolling: function() {
            // Loop-Seite
            if (window.BrandnerRealtime && window.BrandnerRealtime._initialized) {
                window.BrandnerRealtime.POLL_INTERVAL_MS = this.SLOW_POLL_MS;
                if (window.BrandnerRealtime.pollInterval) {
                    clearInterval(window.BrandnerRealtime.pollInterval);
                    window.BrandnerRealtime.pollInterval = setInterval(
                        window.BrandnerRealtime.poll.bind(window.BrandnerRealtime),
                        this.SLOW_POLL_MS
                    );
                }
                debugLog('Loop-Polling verlangsamt auf', this.SLOW_POLL_MS, 'ms');
            }

            // Single Product
            if (window.BrandnerProductRealtime && window.BrandnerProductRealtime.initialized) {
                window.BrandnerProductRealtime.POLL_INTERVAL_MS = this.SLOW_POLL_MS;
                if (window.BrandnerProductRealtime.pollInterval) {
                    clearInterval(window.BrandnerProductRealtime.pollInterval);
                    window.BrandnerProductRealtime.pollInterval = setInterval(
                        window.BrandnerProductRealtime.poll.bind(window.BrandnerProductRealtime),
                        this.SLOW_POLL_MS
                    );
                }
                debugLog('Product-Polling verlangsamt auf', this.SLOW_POLL_MS, 'ms');
            }
        },

        restorePolling: function() {
            // Loop-Seite
            if (window.BrandnerRealtime && window.BrandnerRealtime._initialized) {
                window.BrandnerRealtime.POLL_INTERVAL_MS = this.NORMAL_POLL_MS;
                if (window.BrandnerRealtime.pollInterval) {
                    clearInterval(window.BrandnerRealtime.pollInterval);
                    window.BrandnerRealtime.pollInterval = setInterval(
                        window.BrandnerRealtime.poll.bind(window.BrandnerRealtime),
                        this.NORMAL_POLL_MS
                    );
                }
                window.BrandnerRealtime.poll();
                debugLog('Loop-Polling wiederhergestellt auf', this.NORMAL_POLL_MS, 'ms');
            }

            // Single Product
            if (window.BrandnerProductRealtime && window.BrandnerProductRealtime.initialized) {
                window.BrandnerProductRealtime.POLL_INTERVAL_MS = this.NORMAL_POLL_MS;
                if (window.BrandnerProductRealtime.pollInterval) {
                    clearInterval(window.BrandnerProductRealtime.pollInterval);
                    window.BrandnerProductRealtime.pollInterval = setInterval(
                        window.BrandnerProductRealtime.poll.bind(window.BrandnerProductRealtime),
                        this.NORMAL_POLL_MS
                    );
                }
                window.BrandnerProductRealtime.poll();
                debugLog('Product-Polling wiederhergestellt auf', this.NORMAL_POLL_MS, 'ms');
            }
        },

        // =====================================================================
        // Verbindungs-Optimierung: Idle-Disconnect + Cleanup
        // =====================================================================

        initVisibilityHandler: function() {
            var self = this;

            document.addEventListener('visibilitychange', function() {
                if (document.hidden) {
                    // Leader im Hintergrund: Nach 2 Min abgeben
                    self.idleTimeout = setTimeout(function() {
                        if (!isLeader || self.idleDisconnected) return;
                        debugLog('Leader-Tab idle — gebe Leader ab');
                        self.idleDisconnected = true;

                        // Leader-Rolle abgeben
                        if (bc) {
                            bc.postMessage({ type: 'leader-gone' });
                        }

                        // Pusher trennen
                        if (self.pusher) {
                            self.pusher.disconnect();
                        }

                        isLeader = false;
                        self.connected = false;

                        // Heartbeat stoppen
                        if (heartbeatInterval) {
                            clearInterval(heartbeatInterval);
                            heartbeatInterval = null;
                        }
                    }, self.IDLE_DISCONNECT_MS);
                } else {
                    // Tab wieder aktiv
                    if (self.idleTimeout) {
                        clearTimeout(self.idleTimeout);
                        self.idleTimeout = null;
                    }

                    if (self.idleDisconnected) {
                        self.idleDisconnected = false;

                        // Pruefen ob ein anderer Leader existiert
                        if (bc) {
                            bc.postMessage({ type: 'request-leader', tabId: myTabId });

                            // Kurz warten auf Antwort
                            setTimeout(function() {
                                if (!self.receiving && !isLeader) {
                                    self.becomeLeader();
                                }
                            }, ELECTION_WAIT_MS);
                        } else {
                            self.becomeLeader();
                        }
                    }
                }
            });
        },

        initBeforeUnload: function() {
            var self = this;
            window.addEventListener('beforeunload', function() {
                // Leader-Abgabe signalisieren
                if (bc && isLeader) {
                    bc.postMessage({ type: 'leader-gone' });
                }
                self.destroy();
            });
        },

        // =====================================================================
        // Helpers
        // =====================================================================

        getCurrentUserId: function() {
            if (window.brandnerRealtime && window.brandnerRealtime.currentUserId) {
                return parseInt(window.brandnerRealtime.currentUserId);
            }
            if (window.brandnerProductRealtime && window.brandnerProductRealtime.currentUserId) {
                return parseInt(window.brandnerProductRealtime.currentUserId);
            }
            return 0;
        },

        destroy: function() {
            if (this.idleTimeout) {
                clearTimeout(this.idleTimeout);
                this.idleTimeout = null;
            }
            if (heartbeatInterval) {
                clearInterval(heartbeatInterval);
                heartbeatInterval = null;
            }
            if (leaderCheckInterval) {
                clearInterval(leaderCheckInterval);
                leaderCheckInterval = null;
            }
            if (electionTimeout) {
                clearTimeout(electionTimeout);
                electionTimeout = null;
            }
            if (this.pusher) {
                this.pusher.disconnect();
                this.pusher = null;
            }
            this.channel = null;
            this.connected = false;
            this.receiving = false;
            this.idleDisconnected = false;
            isLeader = false;
        }
    };

    // Globaler Zugriff (fuer Debug und Tests)
    window.BrandnerWebSocket = BrandnerWebSocket;

    // Initialisieren wenn DOM ready
    $(function() {
        setTimeout(function() {
            BrandnerWebSocket.init();
        }, 500);
    });

})(jQuery);
